import { useState, useRef, useEffect } from 'react'
import { useAuth } from "../contexts/AuthContext";
import { Card, Button } from "../components/primitives"
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import useTimedError from '../hooks/useTimedError';

export default function Verify() {
    const { currentUser, verifyCode, sendVerificationCode, refreshLogin } = useAuth();
    const [error, setError] = useTimedError();
    const [loading, setLoading] = useState(false);
    const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
    const navigate = useNavigate();
    const t = useTranslation();
    const [countdown, setCountdown] = useState(0);

    useEffect(() => {
        if (!currentUser) {
            navigate("/login");
        }
    }, [currentUser, navigate]);

    const handleCodeChange = (index, value) => {
        const newCode = [...verificationCode];
        newCode[index] = value;
        setVerificationCode(newCode);
    };

    const combinedCode = verificationCode.join('');

    async function handleCodeSubmit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            await verifyCode(combinedCode);
            setTimeout(async () => { // Why is this needed?
                await refreshLogin();
                navigate("/auth/dashboard", { state: { openWorkspaceRegister: true } });
            }, 3000);
        } catch (error) {
            setError(error.message);
            setLoading(false);
        }
    }

    const handleResend = (e) => {
        e.preventDefault();
        sendVerificationCode();
        setCountdown(60);
    };

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    return (
        <div className="auth-page">
            <div className="auth-outer-container">
                <Card className="auth-container recovery">
                    <Card.Body>
                        <h2 className="text-center mb-4">{t('Verify your email')}</h2>
                        <div className="text-center">{t('Please enter the code sent to {{email}}', { email: currentUser?.email })}</div>
                        <div className="text-center">
                            {countdown > 0 ? (
                                <span>{t('Code sent! Resend available in {{countdown}}s', { countdown })}</span>
                            ) : (
                                <>No code? <a href="#" onClick={handleResend}>{t('Resend')}</a></>
                            )}
                        </div>
                        <form onSubmit={handleCodeSubmit}>
                            <div className="auth-verify-container">
                                {[...Array(6)].map((_, index) => (
                                    <input
                                        key={index}
                                        type="text"
                                        maxLength="1"
                                        value={verificationCode[index]}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^[0-9]$/.test(value) || value === '') {
                                                handleCodeChange(index, value);
                                            }
                                        }}
                                        onPaste={(e) => {
                                            e.preventDefault();
                                            const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                                            const newCode = [...verificationCode];
                                            for (let i = 0; i < pastedData.length; i++) {
                                                if (index + i < 6) {
                                                    newCode[index + i] = pastedData[i];
                                                }
                                            }
                                            setVerificationCode(newCode);
                                            // Focus the next empty input or the last filled input
                                            const nextEmptyIndex = newCode.findIndex((val, idx) => idx >= index && !val);
                                            const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
                                            document.querySelectorAll('.auth-verify-input')[focusIndex].focus();
                                        }}
                                        className="auth-verify-input"
                                        onKeyUp={(e) => {
                                            if (e.target.value && e.target.nextElementSibling) {
                                                e.target.nextElementSibling.focus();
                                            }
                                            if (e.key === "Backspace" && !e.target.value && e.target.previousElementSibling) {
                                                e.target.previousElementSibling.focus();
                                            }
                                        }}
                                        autoFocus={index === 0}
                                    />
                                ))}
                            </div>
                            <Button disabled={loading || combinedCode.length < 6} className="w-100 mt-3" buttonType="submit" error={error} loading={loading}>
                                {error ? error : t('Submit')}
                            </Button>
                        </form>
                    </Card.Body>
                </Card>
            </div>
        </div>
    )
}
